import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ShoppingCart, Star, Wind, Zap, Users, GraduationCap, Gift, Wrench, ChevronRight, Sparkles, Package } from 'lucide-react';
import { productData } from '../mock/productData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import { useCart } from '../context/CartContext';
import KineticHero from '../components/KineticHero';
import Marquee from '../components/Marquee';

const Home = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const navigate = useNavigate();
  const { addToCart, toggleCart, getItemCount } = useCart();
  
  // Product handle for internal routing
  const PRODUCT_HANDLE = 'rotating-windmill-night-light-creative-desktop-ornament-decorative-table-lamp-handmade-metal-craft';

  // Initialize smooth scroll
  useSmoothScroll();

  const handleAddToCart = () => {
    // Add windmill to cart and open cart sidebar
    const windmillProduct = {
      id: 'windmill-001',
      name: productData.name,
      price: productData.price,
      image_url: productData.images[0]
    };
    addToCart(windmillProduct, 1);
    toggleCart();
  };
  
  const handleBuyNow = () => {
    // Add windmill to cart and go to checkout
    const windmillProduct = {
      id: 'windmill-001',
      name: productData.name,
      price: productData.price,
      image_url: productData.images[0]
    };
    addToCart(windmillProduct, 1);
    navigate('/checkout');
  };
  
  const handleDIYComponents = () => navigate('/diy-components');

  // Animated Section Component
  const AnimatedSection = ({ children, className = "", delay = 0 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 60 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
        transition={{ duration: 0.8, delay, ease: [0.6, 0.01, 0.05, 0.95] }}
        className={className}
      >
        {children}
      </motion.div>
    );
  };

  // Feature Card with number
  const FeatureCard = ({ feature, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    const getIcon = (iconName) => {
      const icons = {
        'Lightbulb': Zap,
        'Cog': Wind,
        'Wrench': Wrench,
        'Award': Star,
        'Home': Gift,
        'GraduationCap': GraduationCap,
      };
      const Icon = icons[iconName] || Sparkles;
      return <Icon className="w-8 h-8 text-gold" />;
    };

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
      >
        <Card className="feature-card bg-dark-primary border-gold/10 hover:border-gold/30 transition-all duration-500 h-full group relative overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
          <CardContent className="p-8 relative z-10">
            {/* Manifesto Number */}
            <motion.div 
              className="absolute top-4 right-4 text-6xl font-bold text-gold/10 group-hover:text-gold/20 transition-colors duration-500"
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              {String(index + 1).padStart(2, '0')}
            </motion.div>
            
            <motion.div 
              className="feature-icon-wrapper mb-4"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {getIcon(feature.icon)}
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
            <p className="text-gray-400 leading-relaxed">{feature.description}</p>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="home-container">
      {/* Navigation with blur effect */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
          >
            <Wind className="w-8 h-8 text-gold" />
            <span className="text-2xl font-bold text-gold">MADTOYS</span>
          </motion.div>
          <div className="hidden md:flex items-center gap-8">
            {['Features', 'Gallery', 'Specifications', 'FAQ'].map((item, i) => (
              <motion.a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-gray-300 hover:text-gold transition-colors relative group"
                whileHover={{ y: -2 }}
              >
                {item}
                <motion.span
                  className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold group-hover:w-full transition-all duration-300"
                />
              </motion.a>
            ))}
            <motion.button
              onClick={handleDIYComponents}
              className="text-gray-300 hover:text-gold transition-colors relative group flex items-center gap-2"
              whileHover={{ y: -2 }}
            >
              <Package className="w-4 h-4" />
              DIY Components
              <motion.span
                className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold group-hover:w-full transition-all duration-300"
              />
            </motion.button>
          </div>
          <div className="flex items-center gap-3">
            {/* Cart Icon with Badge */}
            <motion.button
              onClick={toggleCart}
              className="relative p-2 text-gold hover:bg-gold/10 transition-colors rounded-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ShoppingCart className="w-6 h-6" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-dark-primary text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {getItemCount()}
                </span>
              )}
            </motion.button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={handleBuyNow} className="bg-gold hover:bg-gold-dark text-dark-primary font-semibold">
                Buy Now
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.nav>

      {/* Kinetic Hero Section */}
      <KineticHero 
        productData={productData}
        handleBuyNow={handleBuyNow}
        handleAddToCart={handleAddToCart}
      />

      {/* Editorial Marquee */}
      <AnimatedSection className="bg-dark-secondary py-6 border-y border-gold/10">
        <Marquee speed={40}>
          <span className="text-gold text-2xl font-bold mx-8">✨ LIMITED EDITION</span>
          <span className="text-white text-2xl mx-8">•</span>
          <span className="text-gold text-2xl font-bold mx-8">FREE SHIPPING</span>
          <span className="text-white text-2xl mx-8">•</span>
          <span className="text-gold text-2xl font-bold mx-8">44% OFF</span>
          <span className="text-white text-2xl mx-8">•</span>
        </Marquee>
      </AnimatedSection>

      {/* Numbered Manifesto - Features Section */}
      <section id="features" className="py-32 bg-dark-secondary relative overflow-hidden">
        <AnimatedSection>
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Badge className="bg-gold/20 text-gold border-gold/30 text-sm px-4 py-2 mb-4">
                  The Manifesto
                </Badge>
              </motion.div>
              <motion.h2 
                className="text-5xl md:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="text-white">Why Choose </span>
                <span className="hero-gradient-text">MADTOYS</span>
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-400 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                More than just a lamp - it's an experience in engineering, art, and innovation
              </motion.p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {productData.features.map((feature, index) => (
                <FeatureCard key={index} feature={feature} index={index} />
              ))}
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Gallery Section with Video */}
      <section id="gallery" className="py-32 bg-dark-primary">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="text-center mb-20">
              <motion.h2 
                className="text-5xl md:text-6xl font-bold mb-6 text-white"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                See It In Action
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-400"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Watch the mesmerizing rotation and illumination
              </motion.p>
            </div>
          </AnimatedSection>

          {/* Video Showcase with Spotlight Effect */}
          <AnimatedSection delay={0.2}>
            <div className="max-w-4xl mx-auto mb-20">
              <motion.div 
                className="relative overflow-hidden rounded-3xl border-2 border-gold/20 bg-dark-secondary shadow-2xl"
                whileHover={{ scale: 1.02, borderColor: "rgba(212, 175, 55, 0.4)" }}
                transition={{ duration: 0.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/10 via-transparent to-transparent opacity-50" />
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto relative z-10"
                  style={{ display: 'block' }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_visual-product-hub/artifacts/j600sb4x_videoplayback%20%281%29%20%281%29.mp4" type="video/mp4" />
                </video>
              </motion.div>
              <motion.p 
                className="text-center text-gray-400 mt-6 text-sm"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                ✨ Mesmerizing rotation loop
              </motion.p>
            </div>
          </AnimatedSection>

          {/* Image Gallery with Parallax */}
          <div className="grid md:grid-cols-2 gap-12">
            <AnimatedSection delay={0.3}>
              <div className="space-y-6">
                <motion.div 
                  className="gallery-main-image relative overflow-hidden rounded-2xl border-2 border-gold/20"
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={productData.images[selectedImage]} 
                    alt="Product view"
                    className="w-full h-auto object-cover"
                  />
                </motion.div>
                <div className="grid grid-cols-5 gap-4">
                  {productData.images.map((img, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`gallery-thumb rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-gold' : 'border-gray-700'
                      }`}
                      whileHover={{ scale: 1.1, borderColor: "rgba(212, 175, 55, 0.5)" }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                    </motion.button>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.4}>
              <div className="space-y-8">
                {productData.useCases.map((useCase, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card className="bg-dark-secondary border-gold/10 hover:border-gold/30 transition-all duration-500 group">
                      <CardContent className="p-8">
                        <div className="flex items-start gap-4">
                          <motion.span 
                            className="text-4xl font-bold text-gold/20 group-hover:text-gold/40 transition-colors"
                            whileHover={{ scale: 1.2 }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </motion.span>
                          <div>
                            <h3 className="text-2xl font-bold text-gold mb-3">{useCase.title}</h3>
                            <p className="text-gray-300 text-lg leading-relaxed">{useCase.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section id="specifications" className="py-32 bg-dark-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-20">
                <motion.h2 
                  className="text-5xl md:text-6xl font-bold mb-6 text-white"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  Technical Specifications
                </motion.h2>
                <motion.p 
                  className="text-xl text-gray-400"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  Precision engineering meets artistic design
                </motion.p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <Card className="bg-dark-primary border-gold/20 overflow-hidden">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {Object.entries(productData.specifications).map(([key, value], index) => (
                      <motion.div
                        key={index}
                        className="flex items-start justify-between py-4 border-b border-gray-800 last:border-0 group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        whileHover={{ x: 10 }}
                      >
                        <span className="text-gold font-semibold text-lg flex items-center gap-3">
                          <motion.span 
                            className="text-sm text-gold/40"
                            whileHover={{ scale: 1.2 }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </motion.span>
                          {key}
                        </span>
                        <span className="text-gray-300 text-lg text-right max-w-md">{value}</span>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 bg-dark-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-20">
                <motion.h2 
                  className="text-5xl md:text-6xl font-bold mb-6 text-white"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.p 
                  className="text-xl text-gray-400"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  Everything you need to know about your MADTOYS product
                </motion.p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <Accordion type="single" collapsible className="space-y-4">
                {productData.faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <AccordionItem 
                      value={`item-${index}`} 
                      className="bg-dark-secondary border border-gold/10 rounded-lg px-6 hover:border-gold/30 transition-all duration-300"
                    >
                      <AccordionTrigger className="text-lg font-semibold text-white hover:text-gold">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-400 text-base leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Final CTA Section with Parallax */}
      <section className="py-32 bg-dark-secondary relative overflow-hidden">
        <div className="cta-bg-glow" />
        <AnimatedSection>
          <div className="container mx-auto px-6 text-center relative z-10">
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-6 text-white"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to Own This Masterpiece?
            </motion.h2>
            <motion.p 
              className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Limited stock available. Order now and transform your desk into a showcase of engineering brilliance.
            </motion.p>
            <motion.div 
              className="flex items-baseline justify-center gap-3 mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <span className="text-7xl font-bold text-gold">₹{productData.price.toLocaleString('en-IN')}</span>
              <span className="text-gray-500 line-through text-3xl">₹2,499</span>
            </motion.div>
            <motion.div 
              className="flex flex-wrap gap-4 justify-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={handleBuyNow} size="lg" className="bg-gold hover:bg-gold-dark text-dark-primary font-bold text-xl px-12 py-8">
                  Order Now
                  <ChevronRight className="w-6 h-6 ml-2" />
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={handleAddToCart} size="lg" variant="outline" className="border-gold text-gold hover:bg-gold/10 text-xl px-12 py-8">
                  <ShoppingCart className="w-6 h-6 mr-2" />
                  Add to Cart
                </Button>
              </motion.div>
            </motion.div>
            <motion.p 
              className="text-gray-500 mt-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              Free shipping across India • 7-day easy returns
            </motion.p>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="bg-dark-primary border-t border-gold/10 py-12">
        <AnimatedSection>
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <motion.div 
                  className="flex items-center gap-2 mb-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <Wind className="w-6 h-6 text-gold" />
                  <span className="text-xl font-bold text-gold">MADTOYS</span>
                </motion.div>
                <p className="text-gray-400 text-sm">
                  Engineering meets art. Transform your space with mechanical beauty.
                </p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  {['Features', 'Gallery', 'Specifications', 'FAQ'].map((item, i) => (
                    <li key={i}>
                      <a href={`#${item.toLowerCase()}`} className="text-gray-400 hover:text-gold transition-colors">
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Support</h4>
                <ul className="space-y-2">
                  <li><Link to="/shipping" className="text-gray-400 hover:text-gold transition-colors">Shipping Info</Link></li>
                  <li><Link to="/returns" className="text-gray-400 hover:text-gold transition-colors">Returns</Link></li>
                  <li><Link to="/contact" className="text-gray-400 hover:text-gold transition-colors">Contact Us</Link></li>
                  <li><Link to="/privacy" className="text-gray-400 hover:text-gold transition-colors">Privacy Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>Email: info@madtoys.in</li>
                  <li>Phone: +91-9137044474</li>
                  <li>Support: 10 AM - 6 PM IST</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
              <p>&copy; 2025 MADTOYS. All rights reserved. | madtoys.in</p>
            </div>
          </div>
        </AnimatedSection>
      </footer>
    </div>
  );
};

export default Home;
